/**
 * Swatch Sidebar Component
 * Handles the "See More" functionality for variant swatches
 */
class SwatchSidebar {
  maxSwatches = 4; // Maximum swatches to show before "See More"

  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.moveSidebars();
    // Handle configurations immediately after DOM setup
    this.handleConfigurationSwatches();
    // Delay initial variant load to ensure DOM is fully ready
    setTimeout(() => {
      this.handleInitialVariantLoad();
      // Handle configurations again in case DOM changes affected them
      this.handleConfigurationSwatches();
    }, 100);
  }

  handleInitialVariantLoad() {
    // Handle direct variant URLs - ensure selected variants beyond position 4 are visible
    const targetSelectors = document.querySelectorAll('[data-single-option-selector]:checked:not([data-sidebar-swatch])');

    // If no checked selectors found, try to find by form values or URL parameters
    if (targetSelectors.length === 0) {
      // Check URL for variant parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId) {
        // Find the checked sidebar swatches and match them to main swatches
        const checkedSidebarSwatches = document.querySelectorAll('[data-single-option-selector]:checked[data-sidebar-swatch]');

        checkedSidebarSwatches.forEach(sidebarSwatch => {
          const optionIndex = sidebarSwatch.getAttribute('data-index');
          const value = sidebarSwatch.value;

          // Find the corresponding main swatch
          const productContainer = sidebarSwatch.form ?
            sidebarSwatch.form.closest('[data-product], product-component, [data-section-type="product"]') :
            document.querySelector('product-component, [data-section-type="product"]');

          if (productContainer) {
            const mainSwatch = productContainer.querySelector(
              `[data-single-option-selector][data-index="${optionIndex}"][value="${value}"]:not([data-sidebar-swatch])`
            );

            if (mainSwatch) {
              // Check the main swatch
              mainSwatch.checked = true;

              // Find all swatches for this option to determine position
              const allSwatches = Array.from(productContainer.querySelectorAll(
                `[data-single-option-selector][data-index="${optionIndex}"]:not([data-sidebar-swatch])`
              ));

              // Find the position of the selected swatch
              const selectedSwatchPosition = allSwatches.findIndex(swatch => swatch === mainSwatch);

              // If the selected swatch is beyond the 4th position (index 3), it should be made visible
              if (selectedSwatchPosition >= this.maxSwatches && allSwatches.length > this.maxSwatches) {
                this.replaceLastVisibleSwatch(productContainer, optionIndex, mainSwatch);
              }
            }
          }
        });
      }
    }

    // Handle configuration swatches separately since they work differently
    this.handleConfigurationSwatches();

    targetSelectors.forEach(selectedSwatch => {
      const optionIndex = selectedSwatch.getAttribute('data-index');
      const productContainer = selectedSwatch.form ?
        selectedSwatch.form.closest('[data-product], product-component, [data-section-type="product"]') : null;

      if (productContainer) {
        // Find all swatches for this option to determine position
        const allSwatches = Array.from(productContainer.querySelectorAll(
          `[data-single-option-selector][data-index="${optionIndex}"]:not([data-sidebar-swatch])`
        ));

        // Find the position of the selected swatch
        const selectedSwatchPosition = allSwatches.findIndex(swatch => swatch === selectedSwatch);

        // If the selected swatch is beyond the 4th position (index 3), it should be made visible
        if (selectedSwatchPosition >= this.maxSwatches && allSwatches.length > this.maxSwatches) {
          this.replaceLastVisibleSwatch(productContainer, optionIndex, selectedSwatch);
        }
      }
    });
  }

  moveSidebars() {
    // Find all sidebar HTML containers and move them to product level
    const sidebarContainers = document.querySelectorAll('[data-swatch-sidebars-html]');

    sidebarContainers.forEach(container => {
      const productComponent = container.closest('product-component, [data-section-type="product"]');
      if (productComponent) {
        const productId = productComponent.getAttribute('data-section-id') || productComponent.id;
        const targetContainer = document.getElementById(`swatch-sidebars-${productId}`);

        if (targetContainer && container.innerHTML.trim()) {
          // Append the sidebar HTML to the target container (don't overwrite existing content)
          targetContainer.insertAdjacentHTML('beforeend', container.innerHTML);

          // Store product container reference on each sidebar for later use
          const sidebars = targetContainer.querySelectorAll('.swatch__sidebar');
          sidebars.forEach(sidebar => {
            sidebar.setAttribute('data-product-container-id', productId);
          });

          // Re-initialize tooltips for moved content
          const tooltipComponents = targetContainer.querySelectorAll('tooltip-component');
          tooltipComponents.forEach(tooltip => {
            // Trigger the tooltip initialization event
            tooltip.dispatchEvent(new CustomEvent('theme:tooltip:init'));
          });

          // Remove the original container
          container.remove();
        }
      }
    });
  }

  bindEvents() {
    // Handle "See More" button clicks
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-swatch-sidebar-trigger]');
      if (trigger) {
        e.preventDefault();
        this.openSidebar(trigger);
      }
    });

    // Handle sidebar close
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-swatch-sidebar-close]');
      if (closeBtn) {
        e.preventDefault();
        this.closeSidebar();
      }
    });

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeSidebar();
      }
    });

    // Handle sidebar swatch selection
    document.addEventListener('change', (e) => {
      const sidebarSwatch = e.target.closest('[data-sidebar-swatch]');
      if (sidebarSwatch) {
        this.handleSwatchSelection(sidebarSwatch);
      }
    });

    // Sync main swatches with sidebar swatches
    document.addEventListener('change', (e) => {
      const mainSwatch = e.target.closest('[data-single-option-selector]');
      if (mainSwatch && !mainSwatch.hasAttribute('data-sidebar-swatch')) {
        this.syncSwatches(mainSwatch);
      }
    });

    // Handle direct clicks on hidden swatches (fallback)
    document.addEventListener('click', (e) => {
      const hiddenSwatch = e.target.closest('.swatch__button--hidden [data-single-option-selector], .radio__button--hidden [data-single-option-selector]');
      if (hiddenSwatch && !hiddenSwatch.hasAttribute('data-sidebar-swatch')) {
        e.preventDefault();
        const productId = hiddenSwatch.form ? hiddenSwatch.form.closest('[data-product]') : null;
        const optionIndex = hiddenSwatch.getAttribute('data-index');
        if (productId && optionIndex) {
          this.replaceLastVisibleSwatch(productId, optionIndex, hiddenSwatch);
          hiddenSwatch.checked = true;
          hiddenSwatch.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Handle variant changes from URL or programmatic selection
    document.addEventListener('variant:change', () => {
      // Small delay to let variant selection complete
      setTimeout(() => {
        this.handleInitialVariantLoad();
      }, 50);
    });

    // Update data-option-value when swatches are selected
    document.addEventListener('change', (e) => {
      const optionSelector = e.target.closest('[data-single-option-selector]');
      if (optionSelector) {
        this.updateOptionValue(optionSelector);
      }
    });
  }  openSidebar(trigger) {
    const sidebarId = trigger.getAttribute('data-swatch-sidebar-trigger');

    // First try to find the sidebar in the product-level container
    const productComponent = trigger.closest('product-component, [data-section-type="product"]');
    let sidebar = null;

    if (productComponent) {
      const productId = productComponent.getAttribute('data-section-id') || productComponent.id;
      const sidebarContainer = document.getElementById(`swatch-sidebars-${productId}`);
      if (sidebarContainer) {
        sidebar = sidebarContainer.querySelector(`[data-swatch-sidebar="${sidebarId}"]`);
      }
    }

    // Fallback to searching the entire document
    if (!sidebar) {
      sidebar = document.querySelector(`[data-swatch-sidebar="${sidebarId}"]`);
    }

    if (sidebar) {
      sidebar.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus management
      const firstFocusable = sidebar.querySelector('button, input, [tabindex]');
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }
  }

  closeSidebar() {
    const openSidebar = document.querySelector('.swatch__sidebar[aria-hidden="false"]');
    if (openSidebar) {
      openSidebar.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Return focus to trigger button
      const sidebarId = openSidebar.getAttribute('data-swatch-sidebar');
      const trigger = document.querySelector(`[data-swatch-sidebar-trigger="${sidebarId}"]`);
      if (trigger) {
        trigger.focus();
      }
    }
  }

  handleSwatchSelection(sidebarSwatch) {
    // Close sidebar after selection
    this.closeSidebar();

    // Find and sync the corresponding main swatch
    const value = sidebarSwatch.value;
    const optionIndex = sidebarSwatch.getAttribute('data-index');

    // Find the product container using the stored reference
    const sidebar = sidebarSwatch.closest('.swatch__sidebar');
    const productId = sidebar ? sidebar.getAttribute('data-product-container-id') : null;
    let productContainer = null;

    if (productId) {
      productContainer = document.getElementById(productId) || document.querySelector(`[data-section-id="${productId}"]`);
    }

    // Fallback: try to find via form
    if (!productContainer && sidebarSwatch.form) {
      productContainer = sidebarSwatch.form.closest('[data-product], product-component, [data-section-type="product"]');
    }

    if (productContainer) {
      const mainSwatch = productContainer.querySelector(
        `[data-single-option-selector][data-index="${optionIndex}"][value="${value}"]:not([data-sidebar-swatch])`
      );

      if (mainSwatch) {
        // Check if the selected swatch is hidden (beyond maxSwatches'th position)
        const swatchContainer = mainSwatch.closest('.swatches, .radio__button');
        const isHidden = swatchContainer && swatchContainer.classList.contains('swatch__button--hidden');

        if (isHidden) {
          // Replace the maxSwatches'th visible swatch with the selected one
          this.replaceLastVisibleSwatch(productContainer, optionIndex, mainSwatch);
        }

        // Always check and select the main swatch
        if (!mainSwatch.checked) {
          mainSwatch.checked = true;

          // For configuration swatches, trigger the onclick navigation
          if (optionIndex === 'configuration') {
            const label = mainSwatch.nextElementSibling;
            if (label && label.onclick) {
              // Small delay to ensure DOM updates before navigation
              setTimeout(() => {
                label.click();
              }, 10);
            }
          } else {
            mainSwatch.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
  }

  replaceLastVisibleSwatch(productContainer, optionIndex, selectedSwatch) {
    // Find all swatches for this option
    const allSwatches = productContainer.querySelectorAll(
      `[data-single-option-selector][data-index="${optionIndex}"]:not([data-sidebar-swatch])`
    );

    if (allSwatches.length <= this.maxSwatches) {
      return; // No need to replace if 4 or fewer swatches
    }

    // Find currently visible swatches (not hidden)
    const visibleSwatches = Array.from(allSwatches).filter(swatch => {
      const container = swatch.closest('.swatches, .radio__button');
      const isHidden = container && (container.classList.contains('swatch__button--hidden') || container.classList.contains('radio__button--hidden'));
      return container && !isHidden;
    });

    if (visibleSwatches.length >= this.maxSwatches) {
      // Hide the last visible swatch (index maxSwatches-1)
      const lastVisibleSwatch = visibleSwatches[this.maxSwatches - 1];
      const lastVisibleSwatchContainer = lastVisibleSwatch.closest('.swatches, .radio__button');

      if (lastVisibleSwatchContainer) {
        const hiddenClass = lastVisibleSwatchContainer.classList.contains('swatches')
          ? 'swatch__button--hidden'
          : 'radio__button--hidden';

        // Hide instantly
        lastVisibleSwatchContainer.classList.add(hiddenClass);
      }
    }

    // Show the selected swatch - instant, no animation
    const selectedSwatchContainer = selectedSwatch.closest('.swatches, .radio__button');

    if (selectedSwatchContainer) {
      selectedSwatchContainer.classList.remove('swatch__button--hidden', 'radio__button--hidden');
    }
  }

  syncSwatches(mainSwatch) {
    const value = mainSwatch.value;
    const optionIndex = mainSwatch.getAttribute('data-index');
    const productContainer = mainSwatch.form ? mainSwatch.form.closest('[data-product], product-component, [data-section-type="product"]') : null;

    if (productContainer) {
      const sidebarSwatch = productContainer.querySelector(
        `[data-sidebar-swatch][data-index="${optionIndex}"][value="${value}"]`
      );

      if (sidebarSwatch && !sidebarSwatch.checked) {
        sidebarSwatch.checked = true;
      }

      // Check if the selected main swatch was previously hidden and needs to replace the 5th visible
      const wasHidden = mainSwatch.closest('.swatch__button--hidden, .radio__button--hidden');
      if (wasHidden) {
        this.replaceLastVisibleSwatch(productContainer, optionIndex, mainSwatch);
      }
    }
  }

  handleConfigurationSwatches() {
    // Handle configuration swatches that might be beyond the 4th position
    // First try to find by checked state
    let checkedConfigSwatch = document.querySelector('[data-index="configuration"]:checked:not([data-sidebar-swatch])');

    // If no checked swatch found, try to determine from URL (similar to Liquid logic)
    if (!checkedConfigSwatch) {
      const currentPath = window.location.pathname;

      // Find all configuration swatches and check if any handle matches the current path
      const allConfigInputs = document.querySelectorAll('[data-index="configuration"]:not([data-sidebar-swatch])');

      for (const input of allConfigInputs) {
        const handle = input.value;
        if (currentPath.includes(handle)) {
          // "3l-places" is shorter than "3l-places-X". If 3l-places-X is found, override 3l-places
          const isCurrentHandle = !checkedConfigSwatch || checkedConfigSwatch.value.length < handle.length

          if (isCurrentHandle) {
            checkedConfigSwatch = input;
          }
        }
      }

      // Check the sidebar swatch
      document.querySelectorAll(`[data-index="configuration"][value="${checkedConfigSwatch.value}"]`).forEach(swatch => {
        swatch.checked = true
      });
      checkedConfigSwatch.checked = true; // Ensure the found swatch is checked
    }

    if (checkedConfigSwatch) {
      // Try multiple ways to find the product container
      let productContainer = checkedConfigSwatch.closest('product-component, [data-section-type="product"]');

      if (!productContainer) {
        // Fallback: look for any product container in the document
        productContainer = document.querySelector('product-component, [data-section-type="product"]');
      }

      if (!productContainer) {
        // Last resort: look for the product form or any container with product data
        productContainer = document.querySelector('[data-product], .product, .product-single');
      }

      if (productContainer) {
        // Find all visible and hidden configuration swatches in order
        const allConfigSwatches = Array.from(productContainer.querySelectorAll(
          '[data-single-option-selector][data-index="configuration"]:not([data-sidebar-swatch])'
        ));

        if (allConfigSwatches.length > this.maxSwatches) {
          // Find the position of the selected configuration
          const selectedPosition = allConfigSwatches.findIndex(swatch => swatch === checkedConfigSwatch);

          // If the selected configuration is beyond the 4th position (index 3), make it visible
          if (selectedPosition >= this.maxSwatches) {
            this.replaceLastVisibleSwatch(productContainer, 'configuration', checkedConfigSwatch);
          }
        }
      }
    }
  }

  updateOptionValue(optionSelector) {
    // Find the product container
    const productContainer = optionSelector.form ? 
      optionSelector.form.closest('[data-product], product-component, [data-section-type="product"]') :
      optionSelector.closest('[data-product], product-component, [data-section-type="product"]');
    
    if (!productContainer) return;

    // Get the option details
    const optionIndex = optionSelector.getAttribute('data-index');
    const selectedValue = optionSelector.value;
    
    if (!optionIndex || !selectedValue) return;

    // Find the corresponding data-option-value element
    const optionPosition = optionIndex.replace('option', ''); // Convert 'option1' to '1'
    const optionValueElement = productContainer.querySelector(`[data-option-position="${optionPosition}"] [data-option-value]`);
    
    if (optionValueElement) {
      // Update the displayed option value
      optionValueElement.textContent = selectedValue;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SwatchSidebar();
  });
} else {
  new SwatchSidebar();
}

// Handle page changes that might affect configurations
document.addEventListener('page:loaded', () => {
  setTimeout(() => {
    const swatchSidebar = new SwatchSidebar();
  }, 50);
});

// Also initialize for theme editor/Shopify admin
document.addEventListener('shopify:section:load', () => {
  const swatchSidebar = new SwatchSidebar();
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    swatchSidebar.moveSidebars();
    swatchSidebar.handleInitialVariantLoad();
    swatchSidebar.handleConfigurationSwatches();
  }, 100);
});
